import React, { useState } from 'react';
import {
  AppBar,
  Container,
  Toolbar,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Security,
  TrendingUp,
  Close,
  Notifications,
  Settings,
  Logout,
  Person,
  Home,
  Article,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import { CryptoState } from '../../CryptoContext';
import AuthModal from '../Authentication/AuthModal';
import {
  ConnectionProvider,
  WalletProvider,
} from '@solana/wallet-adapter-react';
import {
  WalletModalProvider,
  WalletMultiButton,
} from '@solana/wallet-adapter-react-ui';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { clusterApiUrl } from '@solana/web3.js';
import '@solana/wallet-adapter-react-ui/styles.css';
import newLogo from '../../assets/newLogo.png';
import { colors, typography, spacing, borderRadius, shadows, transitions } from '../../styles/designSystem';

const useStyles = makeStyles((theme) => ({
  appBar: {
    background: 'rgba(10, 10, 10, 0.8)',
    backdropFilter: 'blur(20px)',
    borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
    boxShadow: shadows.glass,
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    transition: transitions.smooth,
  },
  
  toolbar: {
    padding: `0 ${spacing.lg}`,
    minHeight: '80px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.up('lg')]: {
      padding: `0 ${spacing['2xl']}`,
      minHeight: '90px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: `0 ${spacing['3xl']}`,
      minHeight: '100px',
    },
  },
  
  logo: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: transitions.smooth,
    padding: spacing.sm,
    borderRadius: borderRadius.lg,
    '&:hover': {
      transform: 'scale(1.05)',
      background: 'rgba(255, 255, 255, 0.05)',
    },
  },
  
  logoImage: {
    width: 45,
    height: 45,
    marginRight: spacing.md,
    borderRadius: borderRadius.md,
    [theme.breakpoints.up('lg')]: {
      width: 50,
      height: 50,
    },
    [theme.breakpoints.up('xl')]: {
      width: 55,
      height: 55,
    },
  },
  
  logoText: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['2xl'],
    background: colors.gradients.primary,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: typography.letterSpacing.tight,
    [theme.breakpoints.up('lg')]: {
      fontSize: typography.fontSize['3xl'],
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize['4xl'],
    },
  },
  
  navSection: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.lg,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    [theme.breakpoints.up('xl')]: {
      gap: spacing['2xl'],
    },
  },
  
  navItem: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    textTransform: 'none',
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.lg,
    transition: transitions.smooth,
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.up('lg')]: {
      fontSize: typography.fontSize.lg,
      padding: `${spacing.md} ${spacing.lg}`,
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize.xl,
      padding: `${spacing.lg} ${spacing.xl}`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '0%',
      height: '100%',
      background: colors.primary.main,
      transition: 'width 0.3s ease',
      zIndex: -1,
    },
    '&:hover': {
      color: colors.text.primary,
      background: 'rgba(230, 0, 122, 0.1)',
      '&::before': {
        width: '100%',
      },
    },
  },
  
  activeNavItem: {
    color: colors.primary.main,
    fontWeight: typography.fontWeight.semiBold,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '20px',
      height: '2px',
      background: colors.primary.main,
      borderRadius: borderRadius.full,
    },
  },
  
  trustIndicators: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },
  
  trustChip: {
    background: colors.gradients.glass,
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.full,
    backdropFilter: 'blur(10px)',
    transition: transitions.smooth,
    '&:hover': {
      background: 'rgba(230, 0, 122, 0.2)',
      border: `1px solid ${colors.primary.main}`,
    },
  },
  
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    [theme.breakpoints.up('lg')]: {
      gap: spacing.lg,
    },
    [theme.breakpoints.up('xl')]: {
      gap: spacing.xl,
    },
  },
  
  walletButton: {
    background: colors.gradients.primary,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.semiBold,
    fontSize: typography.fontSize.base,
    textTransform: 'none',
    padding: `${spacing.sm} ${spacing.lg}`,
    borderRadius: borderRadius.xl,
    border: 'none',
    transition: transitions.smooth,
    boxShadow: shadows.md,
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.up('lg')]: {
      fontSize: typography.fontSize.lg,
      padding: `${spacing.md} ${spacing.xl}`,
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize.xl,
      padding: `${spacing.lg} ${spacing['2xl']}`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      transition: 'left 0.5s',
    },
    '&:hover': {
      boxShadow: shadows.glow,
      transform: 'translateY(-1px)',
      '&::before': {
        left: '100%',
      },
    },
  },
  
  notificationButton: {
    color: colors.text.primary,
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: borderRadius.lg,
    padding: spacing.sm,
    transition: transitions.smooth,
    '&:hover': {
      background: 'rgba(230, 0, 122, 0.1)',
      transform: 'scale(1.05)',
    },
  },
  
  userAvatar: {
    width: 40,
    height: 40,
    border: `2px solid ${colors.primary.main}`,
    cursor: 'pointer',
    transition: transitions.smooth,
    [theme.breakpoints.up('lg')]: {
      width: 45,
      height: 45,
    },
    [theme.breakpoints.up('xl')]: {
      width: 50,
      height: 50,
    },
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: shadows.glow,
    },
  },
  
  mobileMenuButton: {
    color: colors.text.primary,
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: borderRadius.lg,
    padding: spacing.sm,
    transition: transitions.smooth,
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
    },
    '&:hover': {
      background: 'rgba(230, 0, 122, 0.1)',
      transform: 'scale(1.05)',
    },
  },
  
  drawer: {
    '& .MuiDrawer-paper': {
      background: colors.background.secondary,
      backdropFilter: 'blur(20px)',
      border: `1px solid rgba(255, 255, 255, 0.1)`,
      width: 280,
      padding: spacing.lg,
    },
  },
  
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xl,
    paddingBottom: spacing.lg,
    borderBottom: `1px solid ${colors.border.neutral}`,
  },
  
  drawerNavItem: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    padding: `${spacing.md} ${spacing.lg}`,
    borderRadius: borderRadius.lg,
    marginBottom: spacing.sm,
    transition: transitions.smooth,
    '&:hover': {
      background: 'rgba(230, 0, 122, 0.1)',
      color: colors.primary.main,
    },
  },
  
  userMenu: {
    '& .MuiPaper-root': {
      background: colors.background.secondary,
      backdropFilter: 'blur(20px)',
      border: `1px solid rgba(255, 255, 255, 0.1)`,
      borderRadius: borderRadius.xl,
      boxShadow: shadows.glass,
      marginTop: spacing.sm,
    },
  },
  
  menuItem: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    padding: `${spacing.md} ${spacing.lg}`,
    transition: transitions.smooth,
    '&:hover': {
      background: 'rgba(230, 0, 122, 0.1)',
      color: colors.primary.main,
    },
  },
}));

const ModernHeader = () => {
  const classes = useStyles();
  const history = useHistory();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { user, setUser } = CryptoState();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    setUser(null);
    handleUserMenuClose();
  };

  const renderNavItems = () => (
    <Box className={classes.navSection}>
      <Button
        className={`${classes.navItem} ${history.location.pathname === '/' ? classes.activeNavItem : ''}`}
        onClick={() => handleNavigation('/')}
        startIcon={<Home />}
      >
        Home
      </Button>
      <Button
        className={`${classes.navItem} ${history.location.pathname === '/whitepaper' ? classes.activeNavItem : ''}`}
        onClick={() => handleNavigation('/whitepaper')}
        startIcon={<Article />}
      >
        Whitepaper
      </Button>
    </Box>
  );

  const renderTrustIndicators = () => (
    <Box className={classes.trustIndicators}>
      <Chip
        icon={<Security />}
        label="Audited"
        className={classes.trustChip}
        size="small"
      />
      <Chip
        icon={<TrendingUp />}
        label="Verified"
        className={classes.trustChip}
        size="small"
      />
    </Box>
  );

  const renderUserSection = () => (
    <Box className={classes.userSection}>
      {renderTrustIndicators()}
      
      <ConnectionProvider endpoint={clusterApiUrl('devnet')}>
        <WalletProvider wallets={[new SolflareWalletAdapter()]}>
          <WalletModalProvider>
            <WalletMultiButton className={classes.walletButton} />
          </WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>

      <IconButton className={classes.notificationButton}>
        <Badge badgeContent={3} color="primary">
          <Notifications />
        </Badge>
      </IconButton>

      {user ? (
        <>
          <Avatar
            className={classes.userAvatar}
            onClick={handleUserMenuOpen}
            src={user.photoURL}
          >
            {user.displayName?.charAt(0)}
          </Avatar>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            className={classes.userMenu}
          >
            <MenuItem className={classes.menuItem} onClick={handleUserMenuClose}>
              <Person style={{ marginRight: spacing.sm }} />
              Profile
            </MenuItem>
            <MenuItem className={classes.menuItem} onClick={handleUserMenuClose}>
              <Settings style={{ marginRight: spacing.sm }} />
              Settings
            </MenuItem>
            <Divider style={{ background: colors.border.neutral, margin: spacing.sm }} />
            <MenuItem className={classes.menuItem} onClick={handleLogout}>
              <Logout style={{ marginRight: spacing.sm }} />
              Logout
            </MenuItem>
          </Menu>
        </>
      ) : (
        <Button
          className={classes.walletButton}
          onClick={() => setAuthModalOpen(true)}
          startIcon={<AccountBalanceWallet />}
        >
          Connect Wallet
        </Button>
      )}
    </Box>
  );

  const renderMobileMenu = () => (
    <Drawer
      variant="temporary"
      anchor="right"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      className={classes.drawer}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <Box className={classes.drawerHeader}>
        <Box className={classes.logo}>
          <img src={newLogo} alt="Logo" className={classes.logoImage} />
          <Typography className={classes.logoText}>Retro Llama</Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle} className={classes.notificationButton}>
          <Close />
        </IconButton>
      </Box>

      <List>
        <ListItem
          button
          className={classes.drawerNavItem}
          onClick={() => handleNavigation('/')}
        >
          <ListItemText primary="Home" />
        </ListItem>
        <ListItem
          button
          className={classes.drawerNavItem}
          onClick={() => handleNavigation('/whitepaper')}
        >
          <ListItemText primary="Whitepaper" />
        </ListItem>
      </List>

      <Box style={{ marginTop: 'auto', padding: spacing.lg }}>
        {renderTrustIndicators()}
        {user ? (
          <Button
            fullWidth
            className={classes.walletButton}
            onClick={handleLogout}
            style={{ marginTop: spacing.md }}
          >
            Logout
          </Button>
        ) : (
          <Button
            fullWidth
            className={classes.walletButton}
            onClick={() => {
              setAuthModalOpen(true);
              setMobileOpen(false);
            }}
            startIcon={<AccountBalanceWallet />}
            style={{ marginTop: spacing.md }}
          >
            Connect Wallet
          </Button>
        )}
      </Box>
    </Drawer>
  );

  return (
    <>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <AppBar className={classes.appBar} elevation={0}>
          <Container maxWidth="xl">
            <Toolbar className={classes.toolbar}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Box className={classes.logo} onClick={() => handleNavigation('/')}>
                  <img src={newLogo} alt="Logo" className={classes.logoImage} />
                  <Typography className={classes.logoText}>Retro Llama</Typography>
                </Box>
              </motion.div>

              {renderNavItems()}
              {renderUserSection()}

              <IconButton
                className={classes.mobileMenuButton}
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>
      </motion.div>

      {renderMobileMenu()}

      <AnimatePresence>
        {authModalOpen && (
          <AuthModal
            open={authModalOpen}
            onClose={() => setAuthModalOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ModernHeader; 